import type { Comparison } from "../../lib/comparisons";
import { no, partial, unstated, yes } from "./cells";

export const mesenvies: Comparison = {
  name: "Mes Envies",
  site: "mesenvies.fr",
  linkLabel: "Alternative à Mes Envies",
  checkedOn: "2026-09-29",
  seoTitle: "Alternative à Mes Envies : Listou ou Mes Envies ? Comparatif 2026 · Listou",
  seoDescription:
    "Listou et Mes Envies sont gratuits et multi-boutiques. Listou se personnalise de A à Z, garde la surprise jusqu'à la naissance et se gère à deux. Le comparatif point par point.",
  h1: "Listou ou Mes Envies : quelle liste de naissance choisir ?",
  summary:
    "Listou et Mes Envies sont deux listes de cadeaux gratuites, où l'on ajoute des articles de n'importe quelle boutique et où les proches réservent sans créer de compte. La différence tient au design, à la surprise et au partage : une liste Listou se personnalise entièrement (modèle de page, couleurs, police, stickers), les parents ne voient pas ce qui a été réservé avant l'événement, et la liste se gère à deux. Mes Envies, en ligne depuis 2006, permet de choisir une photo d'en-tête et la couleur des boutons, et peut afficher un IBAN pour les participations.",
  rows: [
    {
      feature: "Prix",
      listou: yes("Gratuit, sans limite de réservations"),
      them: yes("Gratuit, sans limite de fonctions ni de durée"),
    },
    {
      feature: "Personnaliser le design de la liste",
      listou: yes("6 modèles de page, 13 palettes, 4 polices, 162 stickers, fonds à motifs, photo"),
      them: partial("Photo d'en-tête et couleur des liens et boutons"),
    },
    {
      feature: "Compte pour offrir",
      listou: yes("Aucun : le lien suffit"),
      them: yes("Aucun : un nom, l'email en option"),
    },
    {
      feature: "Compte pour créer la liste",
      listou: yes("Un email ou Google, sans mot de passe"),
      them: yes("Prénom, nom et mot de passe, email facultatif"),
    },
    {
      feature: "Articles de n'importe quelle boutique",
      listou: yes("Coller le lien : photo et prix récupérés"),
      them: yes("Oui, ou à la main"),
    },
    {
      feature: "Visuels à partager",
      listou: yes("QR code pour le faire-part, visuels pour Instagram"),
      them: partial("Lien, par Facebook, WhatsApp, Twitter ou email"),
    },
    {
      feature: "Les parents ne voient pas ce qui est réservé",
      listou: yes("Oui, par défaut, jusqu'au lendemain de l'événement"),
      them: no("Non : l'espace de gestion liste les cadeaux réservés"),
    },
    {
      feature: "Offrir sans que les parents sachent qui",
      listou: yes("Oui, même après la révélation"),
      them: partial("Nom caché aux autres visiteurs, pas à l'auteur de la liste"),
    },
    {
      feature: "Gérer la liste à deux",
      listou: yes("Co-propriétaire, et invités qui peuvent modifier"),
      them: unstated("Pas de co-gestion décrite dans l'aide"),
    },
    {
      feature: "Participation en argent",
      listou: partial("Lien vers votre cagnotte (Leetchi, Lydia…)"),
      them: yes("RIB ou lien de cagnotte, sans frais Mes Envies"),
    },
    {
      feature: "Plusieurs exemplaires d'un article",
      listou: yes("Quantité, chacun en réserve une partie"),
      them: partial("Participations partielles, par don libre"),
    },
    {
      feature: "Secret Santa",
      listou: yes("Oui, avec exclusions, et des listes en groupe"),
      them: yes("Oui, sans compte, avec exclusions"),
    },
    {
      feature: "Occasions",
      listou: yes("Naissance, Noël, anniversaire, mariage"),
      them: yes("Naissance, Noël, mariage et autres occasions"),
    },
  ],
  body: `
## Une liste à votre image

Une liste de naissance, on l'envoie à toute la famille, on l'imprime sur le faire-part, on la montre sur Instagram. Elle mérite mieux qu'une page standard.

Sur **Listou**, tout se règle dans l'onglet Apparence, avec l'aperçu en direct :

- **6 modèles de page** : la mise en page elle-même change, pas seulement les couleurs.
- **13 palettes**, plus une couleur d'accent au choix.
- **4 polices** pour les titres : ronde, élégante, manuscrite ou éditoriale.
- **162 stickers** dessinés, en 18 thèmes (naissance, Noël, mariage…), pour décorer l'en-tête ou tapisser le fond en motifs.
- **Une photo de couverture** et un emoji pour la liste.

**Mes Envies** propose deux réglages : une photo d'en-tête, la vôtre ou une suggestion par thème, et la couleur des liens et des boutons. Leur aide conseille d'éviter les couleurs trop claires, pour que le texte blanc des boutons reste lisible.

## La surprise gardée jusqu'au bout

C'est le point qui sépare le plus les deux services.

Sur **Mes Envies**, l'email envoyé à chaque réservation ne dit pas qui offre quel cadeau, « au cas où vous souhaiteriez garder la surprise ». Mais l'espace de gestion de la liste affiche la liste de tous les cadeaux réservés. Il suffit de l'ouvrir pour savoir ce qui vous attend. Il n'existe pas de réglage pour le cacher.

Sur **Listou**, le mode surprise est activé par défaut. Tant qu'il est actif, ni vous ni votre co-parent ne voyez ce qui est réservé, ni par qui. Vous recevez seulement « un cadeau vient d'être réservé ». La révélation a lieu le lendemain de la date de l'événement, ou quand vous le décidez. Si vous ouvrez le lien de votre propre liste, Listou vous montre l'aperçu que voient vos proches, sans les réservations.

Et si vous préférez tout voir, par exemple pour la liste d'anniversaire d'un enfant dont vous achetez aussi une partie des cadeaux, le mode surprise se coupe en un clic.

## Offrir anonymement

Sur Mes Envies, la personne qui offre peut masquer son nom de la mention « Déjà offert par », mais seulement pour les autres visiteurs : l'aide précise que « seul l'auteur de la liste pourra voir votre nom ».

Sur Listou, cocher « Rester anonyme » cache le nom à la personne qui reçoit, y compris après la révélation : elle voit « un proche ». Les autres invités ne voient jamais le prénom, seulement que le cadeau est pris.

## Préparer la liste à deux

Une liste de naissance se fait rarement seul. Sur Listou, vous invitez le co-parent comme **co-propriétaire** : il a les mêmes droits que vous, ajoute des articles depuis son téléphone, et la surprise reste gardée pour vous deux. Vous pouvez aussi inviter la marraine ou une amie à « modifier » : elle aide à remplir la liste sans toucher au partage ni à la surprise.

Sur Mes Envies, le compte comporte des champs pour le prénom et le nom du conjoint, qui servent à retrouver la liste. L'aide ne décrit pas de moyen de la gérer à plusieurs comptes.

## Ajouter des articles

Les deux services acceptent n'importe quelle boutique : grand magasin, site d'un créateur, occasion. On peut aussi ajouter une envie à la main, pour un cadeau fait main ou repéré en magasin.

Sur **Listou**, on colle le lien du produit, depuis son téléphone comme depuis son ordinateur : le nom, la photo et le prix sont récupérés. Pour les idées, Listou propose un assistant selon l'occasion (« les tailles 3, 6 et 12 mois, le thermomètre de bain ») et les articles que d'autres parents ont déjà ajoutés à leurs listes, avec leurs avis. Trois niveaux d'envie, « Coup de cœur », « Envie » et « Idée », disent à vos proches ce qui compte le plus.

## Partager la liste

Sur **Mes Envies**, on partage le lien de la liste par Facebook, WhatsApp, Twitter ou email, ou on le recopie soi-même, par exemple sur un faire-part.

Sur **Listou**, l'onglet Partage donne le lien, un **QR code** à imprimer sur le faire-part, et des **visuels prêts pour Instagram**. Le lien a été trop partagé ? Vous en générez un nouveau, et l'ancien cesse de fonctionner.

## Ce que vivent vos proches

C'est souvent la grand-mère qui décide si une liste « marche ». Sur les deux services, elle ouvre le lien et réserve sans créer de compte. Mes Envies demande un nom et, en option, un email. Listou demande un prénom et un email, facultatifs tous les deux.

Sur Listou, un cadeau réservé apparaît aussitôt comme pris pour les autres invités. Pour un article voulu en plusieurs exemplaires (trois bodies, deux assiettes), chacun en réserve une partie et la liste indique combien il en reste. Si vous avez indiqué une adresse de livraison, elle n'apparaît qu'après la réservation, jamais sur la liste.

## Les participations en argent

Aucun des deux ne fait transiter d'argent.

**Mes Envies** propose un champ « Compte bancaire ou lien vers cagnotte ». Vous pouvez y mettre votre IBAN, qui est communiqué à chaque personne qui réserve, ou un lien PayPal, Leetchi, Lydia ou autre. Mes Envies ne prend aucun frais ; les services de cagnotte, eux, « demandent généralement une commission ».

**Listou** propose d'ajouter un lien vers votre cagnotte. Un bloc « Participer librement » apparaît en bas de la liste. Il n'y a pas de champ pour un IBAN.

Si vous voulez recevoir les participations par virement direct, sans service tiers, Mes Envies est plus simple.

## Noël et les fêtes à plusieurs

Les deux proposent un tirage au sort de Secret Santa gratuit, avec exclusions (pour qu'un couple ne se tire pas lui-même).

Listou va plus loin pour les fêtes de famille : un **groupe** réunit plusieurs listes derrière un seul lien, « Noël chez les Martin ». Chacun choisit son prénom, remplit sa liste et réserve sur celles des autres. Le tirage au sort peut s'y ajouter.

## Passer de Mes Envies à Listou

Il n'y a pas d'import automatique. La migration prend quelques minutes :

1. Créez votre liste sur Listou, avec la date de l'événement.
2. Pour chaque article, copiez le lien de la boutique depuis votre liste Mes Envies et collez-le dans Listou. Le nom, la photo et le prix sont récupérés.
3. Invitez votre co-parent dans les réglages.
4. Envoyez le nouveau lien à vos proches, ou imprimez son QR code sur le faire-part.

Si des cadeaux ont déjà été réservés sur Mes Envies, retirez-les de la nouvelle liste pour éviter les doublons.
`,
  chooseThem: [
    "Vous voulez que votre IBAN s'affiche directement aux invités, sans service de cagnotte.",
    "Vous préférez un service installé de longue date : Mes Envies existe depuis 2006.",
  ],
  chooseListou: [
    "Vous voulez une liste qui vous ressemble : modèle, couleurs, police et stickers.",
    "Vous voulez découvrir les cadeaux à la naissance, sans savoir à l'avance qui offre quoi.",
    "Vous préparez la liste à deux, chacun depuis son téléphone.",
    "Vos proches offrent parfois anonymement, et ce choix doit être respecté jusqu'au bout.",
    "Vous organisez aussi Noël en famille : listes en groupe et Secret Santa au même endroit.",
  ],
  faq: [
    [
      "Listou est-il une bonne alternative à Mes Envies ?",
      "Oui, surtout si vous tenez au design de votre liste, à la surprise ou à la préparer à deux. Les deux sont gratuits, acceptent n'importe quelle boutique et ne demandent pas de compte aux proches. Listou se personnalise entièrement, cache les réservations aux parents jusqu'après l'événement et permet un co-propriétaire ; Mes Envies peut afficher votre IBAN aux invités.",
    ],
    [
      "Peut-on personnaliser le design de sa liste ?",
      "Sur Listou, oui, entièrement : 6 modèles de page, 13 palettes et une couleur d'accent, 4 polices de titre, 162 stickers en 18 thèmes, des fonds à motifs et une photo de couverture. Sur Mes Envies, on choisit une photo d'en-tête et la couleur des liens et boutons.",
    ],
    [
      "Sur Mes Envies, voit-on les cadeaux réservés ?",
      "Oui. D'après l'aide de Mes Envies, l'email de réservation ne dit pas qui offre quoi, mais l'espace de gestion affiche la liste de tous les cadeaux réservés. Sur Listou, le mode surprise, activé par défaut, les cache jusqu'au lendemain de l'événement.",
    ],
    [
      "Peut-on gérer une liste Mes Envies à deux ?",
      "L'aide de Mes Envies ne décrit pas de co-gestion : le compte a des champs pour le nom du conjoint, qui servent à retrouver la liste. Sur Listou, le co-parent devient co-propriétaire, avec les mêmes droits.",
    ],
    [
      "Faut-il un compte pour offrir un cadeau ?",
      "Non, ni sur Listou ni sur Mes Envies. Les proches ouvrent le lien et réservent. Mes Envies demande un nom ; sur Listou, le prénom et l'email sont facultatifs.",
    ],
    [
      "Peut-on recevoir de l'argent plutôt que des cadeaux ?",
      "Sur Mes Envies, vous pouvez afficher votre IBAN ou un lien de cagnotte aux personnes qui réservent. Sur Listou, vous ajoutez un lien vers votre cagnotte (Leetchi, Lydia, PayPal…). Aucun des deux ne fait transiter d'argent.",
    ],
    [
      "Peut-on passer de Mes Envies à Listou ?",
      "Il n'y a pas d'import automatique. Recréez la liste sur Listou en collant les liens de vos articles : le nom, la photo et le prix sont récupérés à chaque fois.",
    ],
    [
      "Listou et Mes Envies sont-ils vraiment gratuits ?",
      "Oui, les deux. Listou se rémunère par une commission que certaines boutiques versent quand un proche achète via la liste, sans changer le prix.",
    ],
    [
      "Lequel choisir pour une liste de Noël en famille ?",
      "Les deux font le Secret Santa. Listou permet en plus de réunir les listes de toute la famille derrière un seul lien, où chacun choisit son prénom et réserve sur les listes des autres.",
    ],
  ],
  sources: [
    { label: "Mes Envies : page d'accueil", url: "https://www.mesenvies.fr/" },
    {
      label: "Mes Envies : est-ce 100 % gratuit ?",
      url: "https://aide.mesenvies.fr/hc/331138012/25/est-ce-100-gratuit",
    },
    {
      label: "Mes Envies : faut-il un compte pour offrir un cadeau ?",
      url: "https://aide.mesenvies.fr/hc/331138012/50/est-ce-que-je-dois-creer-un-compte-pour-offrir-un-cadeau",
    },
    {
      label: "Mes Envies : modifier les identifiants de votre compte",
      url: "https://aide.mesenvies.fr/hc/331138012/14/modifier-les-identifiants-de-votre-compte",
    },
    {
      label: "Mes Envies : gérer les réservations et dons libres",
      url: "https://aide.mesenvies.fr/hc/331138012/5/gerer-les-reservations-et-dons-libres",
    },
    {
      label: "Mes Envies : cacher son nom dans « Déjà offert par »",
      url: "https://aide.mesenvies.fr/hc/331138012/49/comment-cacher-mon-nom-dans-la-mention-deja-offert-par",
    },
    {
      label: "Mes Envies : ajouter une envie cadeau",
      url: "https://aide.mesenvies.fr/hc/331138012/2/ajouter-une-envie-cadeau",
    },
    {
      label: "Mes Envies : personnaliser l'aspect visuel de votre liste",
      url: "https://aide.mesenvies.fr/hc/331138012/7/personnaliser-laspect-visuel-de-votre-liste",
    },
    {
      label: "Mes Envies : partager votre liste",
      url: "https://aide.mesenvies.fr/hc/331138012/12/partager-votre-liste",
    },
    {
      label: "Mes Envies : recevoir des dons libres",
      url: "https://aide.mesenvies.fr/hc/331138012/4/recevoir-des-dons-libres-ou-des-participations-partielles",
    },
    {
      label: "Mes Envies : créer une ou plusieurs listes",
      url: "https://aide.mesenvies.fr/hc/331138012/6/creer-ou-supprimer-une-ou-plusieurs-listes",
    },
    { label: "Mes Envies : Secret Santa", url: "https://www.mesenvies.fr/secret-santa.html" },
    { label: "Mes Envies : liste de Noël", url: "https://www.mesenvies.fr/liste-noel.html" },
    { label: "Mes Envies : liste de mariage", url: "https://www.mesenvies.fr/liste-mariage.html" },
  ],
};
